import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./dialog";
import { Button } from "./button";
import { RadioGroup, RadioGroupItem } from "./radio-group";
import { Label } from "./label";
import { Separator } from "./separator";

interface UnitsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (settings: UnitSettings) => void;
}

export interface UnitSettings {
  chartScaling: "different" | "same";
  measurementSystem: "metric" | "imperial";
  power: "kw" | "hp";
  torque: "nm" | "lbft";
  distance: "km" | "miles";
  displacement: "ccm" | "cu";
  mass: "kg" | "lbs";
  pressure: "kpa" | "psi";
  temperature: "c" | "f";
  dateFormat: "metric" | "imperial";
}

export const UnitsModal = ({ open, onOpenChange, onSave }: UnitsModalProps) => {
  const [settings, setSettings] = React.useState<UnitSettings>({
    chartScaling: "different",
    measurementSystem: "metric",
    power: "kw",
    torque: "nm",
    distance: "km",
    displacement: "ccm",
    mass: "kg",
    pressure: "kpa",
    temperature: "c",
    dateFormat: "metric",
  });

  const handleSave = () => {
    onSave(settings);
    onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleSystemChange = (system: "metric" | "imperial") => {
    setSettings({
      ...settings,
      measurementSystem: system,
      power: system === "metric" ? "kw" : "hp",
      torque: system === "metric" ? "nm" : "lbft",
      distance: system === "metric" ? "km" : "miles",
      displacement: system === "metric" ? "ccm" : "cu",
      mass: system === "metric" ? "kg" : "lbs",
      pressure: system === "metric" ? "kpa" : "psi",
      temperature: system === "metric" ? "c" : "f",
      dateFormat: system === "metric" ? "metric" : "imperial",
    });
  };

  const currentDate = new Date();
  const metricDate = currentDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }) + " " + currentDate.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const imperialDate = currentDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }) + " " + currentDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Your view setting</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div>
            <h3 className="font-medium mb-4">Chart scaling power and torque</h3>
            <RadioGroup
              value={settings.chartScaling}
              onValueChange={(value: "different" | "same") =>
                setSettings({ ...settings, chartScaling: value })
              }
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="different" id="different" />
                <Label htmlFor="different">Different</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="same" id="same" />
                <Label htmlFor="same">Same</Label>
              </div>
            </RadioGroup>
          </div>

          <Separator />

          <div>
            <h3 className="font-medium mb-4">Units of measurement</h3>
            <RadioGroup
              value={settings.measurementSystem}
              onValueChange={handleSystemChange}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="metric" id="metric" />
                <Label htmlFor="metric">Metric</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="imperial" id="imperial" />
                <Label htmlFor="imperial">Imperial</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>Power:</div>
            <div>{settings.measurementSystem === "metric" ? "Kilowatt (kW) (hp)" : "Horsepower"}</div>
            
            <div>Torque:</div>
            <div>{settings.measurementSystem === "metric" ? "Newton metre (Nm)" : "Foot pound-force (Lbft)"}</div>
            
            <div>Distance:</div>
            <div>{settings.measurementSystem === "metric" ? "Kilometre (Km)" : "Miles (M)"}</div>
            
            <div>Displacement:</div>
            <div>{settings.measurementSystem === "metric" ? "Cubic centimetre (Ccm)" : "Cubic inch (cu)"}</div>
            
            <div>Mass:</div>
            <div>{settings.measurementSystem === "metric" ? "Kilogram (Kg)" : "Pound (lbs)"}</div>
            
            <div>Pressure:</div>
            <div>{settings.measurementSystem === "metric" ? "Kilopascal (Kpa)" : "Pounds per square inch (Psi)"}</div>
            
            <div>Temperature:</div>
            <div>{settings.measurementSystem === "metric" ? "Celsius (C)" : "Fahrenheit (F)"}</div>
            
            <div>Date and time:</div>
            <div>{settings.measurementSystem === "metric" ? metricDate : imperialDate}</div>
          </div>
        </div>
        <DialogFooter className="flex justify-between">
          <Button variant="outline" onClick={handleCancel}>
            CANCEL
          </Button>
          <div className="space-x-2">
            <Button onClick={handleSave}>
              SAVE
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              CLOSE
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
